import { useGlobalStore } from '@highlight/ipc/react'
import { useMessageMentions } from '../components/chat/input/mentions/atoms'
import { useNextMessageModelId } from '../store/messages'
import { useModels } from '../store/models'
import type { MentionableModel } from '../types'
import { useModelChoice } from './use-plan-features'

export interface NextMessageModel {
  label: string
  /** The model's max input tokens. */
  contextWindow: number
}

/**
 * The model the chat's next message will use (a composer @mention, then the
 * chat's last mentioned model, then the default) and its context window.
 * Undefined until the model list knows that model's window.
 */
export function useNextMessageModel(chatId: string | undefined): NextMessageModel | undefined {
  const model = useNextMessageModelEntry(chatId)
  const contextWindow = model?.limits?.maxInputTokens
  if (!model || !contextWindow || contextWindow <= 0) {
    return undefined
  }
  return { label: model.label, contextWindow }
}

/**
 * Whether a reasoning effort changes how the next message's model thinks. True
 * while the model is unknown, since the server's default model takes one.
 */
export function useNextMessageModelTakesReasoningEffort(chatId: string | undefined): boolean {
  const model = useNextMessageModelEntry(chatId)
  if (!model) {
    return true
  }
  return model.capabilities?.supportsReasoningEffort === true
}

function useNextMessageModelEntry(chatId: string | undefined): MentionableModel | undefined {
  const mentions = useMessageMentions()
  const [defaultModel] = useGlobalStore<string>('settings', 'defaultModel')
  const models = useModels()
  const modelId = useNextMessageModelId(chatId, { mentions, defaultModel, modelChoice: useModelChoice() })
  return modelId ? models.find((candidate) => candidate.id === modelId) : undefined
}
